import {CommandBase} from "./commandBase";
import {RemoteConstantsModel} from "../models/remoteConstants.model";
import {RemoveLocalizationValueCommand} from "./removeLocalizationValue.command";
import {collectTechRemovals, pruneRoutesTo, ROOT_ID} from "../utils/techTree.utils";

export class RemoveTechNodeCommand extends CommandBase {
    id: string;

    constructor(id: string) {
        super();
        this.id = id;
    }

    // Deletes the node plus every descendant that this node was the only route to.
    // Descendants another parent still reaches are kept — see collectTechRemovals.
    //
    // The whole removal set is computed up front and applied in one pass, never by
    // recursing per node: the previous version cached each node's index in
    // data.techTree and then let nested calls splice that same array, so after the
    // first child was removed the loop was reading a different node's children and
    // the final splice dropped whichever node had shifted into the captured slot.
    // It also walked a children array that those nested calls were removing entries
    // from, which skipped every other child. Together those left live nodes behind
    // with all their parents gone — the orphan with the red outline.
    execute(data: RemoteConstantsModel): boolean {
        if (this.id === ROOT_ID) return false;              // the anchor isn't deletable
        if (!data.techTree?.some(t => t.id === this.id)) return false;

        const removals = collectTechRemovals(data.techTree, this.id);

        // Drop the nodes. Backwards so the splices can't shift an index we still
        // have to visit.
        for (let i = data.techTree.length - 1; i >= 0; i--) {
            if (removals.has(data.techTree[i].id)) data.techTree.splice(i, 1);
        }

        // Then strip every reference to them from the survivors.
        for (const tech of data.techTree) {
            if (!tech.children) continue;
            for (let i = tech.children.length - 1; i >= 0; i--) {
                if (removals.has(tech.children[i])) tech.children.splice(i, 1);
            }
        }

        for (const id of Array.from(removals)) {
            // Authored edge routes pointing at a removed node, or the model
            // accumulates records for edges that no longer exist.
            pruneRoutesTo(data, id);
            new RemoveLocalizationValueCommand(id).execute(data);
            new RemoveLocalizationValueCommand(`${id}.description`).execute(data);
        }

        return true;
    }
}
